// /docs and /docs/:slug — the documentation section. A TOC rail (all pages,
// grouped by section, with a type-to-filter box) beside the page content, so
// everything is findable at a glance; task-named page titles do the rest.
// Content pages live in src/docs/pages/*, registered in src/docs/registry.
// /docs and unknown slugs land on the first page (the quick start).

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, NavLink, useParams } from "react-router-dom";
import {
  IconArrowLeft,
  IconArrowRight,
  IconChevronDown,
  IconSearch,
} from "@tabler/icons-react";
import { useShellHeader } from "../app/shell-header";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { cn } from "@/lib/utils";
import { docBodyText, snippetFor } from "../docs/search";
import {
  ALL_DOCS,
  DOC_SECTIONS,
  findDoc,
  sectionOf,
  type DocPage,
  type DocSectionEntry,
} from "../docs/registry";

/** Case-insensitive match against title, blurb, keywords, section label, and
 *  the page's full body text (see docs/search.ts). */
function matches(page: DocPage, section: DocSectionEntry, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack =
    [page.title, page.navTitle ?? "", page.blurb, section.label, ...(page.keywords ?? [])]
      .join(" ")
      .toLowerCase() +
    " " +
    docBodyText(page).toLowerCase();
  return q.split(/\s+/).every((term) => haystack.includes(term));
}

/** The TOC: filter box + section-grouped page links. Used in the md+ rail and
 *  the small-screen collapsible; `onNavigate` lets the latter close itself. */
function DocsToc({
  query,
  onQuery,
  onNavigate,
}: {
  query: string;
  onQuery: (q: string) => void;
  onNavigate?: () => void;
}) {
  const sections = DOC_SECTIONS.map((s) => ({
    ...s,
    pages: s.pages.filter((p) => matches(p, s, query)),
  })).filter((s) => s.pages.length > 0);

  return (
    <nav aria-label="Documentation" className="flex flex-col gap-6">
      <div className="relative">
        <IconSearch className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search docs…"
          aria-label="Search docs"
          className="pl-8"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
        />
      </div>
      {sections.length === 0 && (
        <p className="px-3 text-sm text-muted-foreground">No pages match.</p>
      )}
      {sections.map((s) => (
        <div key={s.label} className="flex flex-col gap-1">
          <span className="px-3 pb-0.5 font-mono text-[0.625rem] font-semibold tracking-widest text-foreground uppercase">
            {s.label}
          </span>
          {s.pages.map((p) => {
            // With an active query, each hit shows a body-text excerpt with
            // the matched term highlighted — a bare title list looks like
            // the search ignored the content. Two-line result entries get a
            // softer corner than the one-line pill (a wrapped pill loses
            // its shape).
            const snip = query.trim() ? snippetFor(p, query) : null;
            return (
              <NavLink
                key={p.slug}
                to={`/docs/${p.slug}`}
                onClick={onNavigate}
                title={p.blurb}
                className={({ isActive }) =>
                  cn(
                    "px-3 text-sm transition-colors",
                    snip ? "rounded-2xl py-2" : "truncate rounded-full py-1.5",
                    isActive
                      ? "bg-accent font-medium text-accent-foreground"
                      : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
                  )
                }
              >
                {snip ? (
                  <>
                    <span className="block truncate">{p.navTitle ?? p.title}</span>
                    <span className="mt-0.5 line-clamp-2 block text-xs font-normal text-muted-foreground">
                      {snip.pre}
                      <mark className="rounded-sm bg-primary/15 px-0.5 font-medium text-foreground">
                        {snip.hit}
                      </mark>
                      {snip.post}
                    </span>
                  </>
                ) : (
                  p.navTitle ?? p.title
                )}
              </NavLink>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

export function DocsPage() {
  const params = useParams();
  const slug = params.slug ?? "";
  const doc = findDoc(slug);
  const section = sectionOf(slug);
  const [query, setQuery] = useState("");
  // Small screens: the TOC lives in a collapsible above the content.
  const [tocOpen, setTocOpen] = useState(false);

  // The content column scrolls internally (the TOC rail stays fixed), so a
  // page change must reset its scroll position explicitly. scrollTop, not
  // scrollTo() — jsdom implements only the property.
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [slug]);

  const { prev, next } = useMemo(() => {
    const i = ALL_DOCS.findIndex((p) => p.slug === slug);
    return {
      prev: i > 0 ? ALL_DOCS[i - 1] : undefined,
      next: i >= 0 && i < ALL_DOCS.length - 1 ? ALL_DOCS[i + 1] : undefined,
    };
  }, [slug]);

  useShellHeader(
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          {doc ? (
            <BreadcrumbLink render={<Link to="/docs" />}>Docs</BreadcrumbLink>
          ) : (
            <BreadcrumbPage>Docs</BreadcrumbPage>
          )}
        </BreadcrumbItem>
        {doc && (
          <>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="max-w-56 truncate">{doc.title}</BreadcrumbPage>
            </BreadcrumbItem>
          </>
        )}
      </BreadcrumbList>
    </Breadcrumb>,
    null,
    [doc?.slug],
  );

  // /docs and unknown slugs land on the first page. (After the hooks — hook
  // order must not depend on the slug.)
  if (!doc || !section) {
    return <Navigate to={`/docs/${ALL_DOCS[0]?.slug ?? ""}`} replace />;
  }

  return (
    // Viewport-fit (same height math as the vault view: 100svh minus the
    // shell header + inset margins) so the TOC rail stays put while ONLY the
    // content column scrolls. The rail is pinned left; the article centers
    // itself in whatever width remains.
    <div className="flex h-[calc(100svh-4.5rem)] w-full gap-8 px-6">
      {/* md+ rail: fixed beside the scrolling content; scrolls itself only if
          the TOC outgrows the viewport. px-1: content must not sit at the
          clip edge, or borders/focus rings get shaved. */}
      <aside className="hidden w-56 shrink-0 overflow-y-auto overscroll-contain px-1 py-6 md:block">
        <DocsToc query={query} onQuery={setQuery} />
      </aside>

      {/* The scroll container is NOT itself the flex column: with a fixed
          height, flex would shrink the cards to fit instead of letting them
          overflow, leaving nothing to scroll. px-1 keeps card borders off
          the clip edge. */}
      <div
        ref={contentRef}
        className="min-w-0 flex-1 overflow-y-auto overscroll-contain px-1 py-6"
      >
        <div className="mx-auto flex max-w-3xl flex-col gap-6">
        {/* Small screens: collapsible TOC above the content. */}
        <div className="md:hidden">
          <Button
            variant="outline"
            className="w-full justify-between"
            aria-expanded={tocOpen}
            onClick={() => setTocOpen((o) => !o)}
          >
            Browse docs
            <IconChevronDown className={cn("transition-transform", tocOpen && "rotate-180")} />
          </Button>
          {tocOpen && (
            <div className="mt-3 rounded-md border p-3">
              <DocsToc query={query} onQuery={setQuery} onNavigate={() => setTocOpen(false)} />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <PageHeader eyebrow={section.label} title={doc.title} />
          <p className="text-sm text-muted-foreground">{doc.blurb}</p>
        </div>

        <doc.Component />

        {/* Reading-order navigation. */}
        {(prev || next) && (
          <div className="mt-2 flex items-stretch justify-between gap-3 border-t pt-4">
            {prev ? (
              <Button
                variant="outline"
                className="h-auto min-w-0 flex-col items-start gap-0.5 py-2"
                render={<Link to={`/docs/${prev.slug}`} />}
              >
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <IconArrowLeft className="size-3.5" /> Previous
                </span>
                <span className="truncate">{prev.title}</span>
              </Button>
            ) : (
              <span />
            )}
            {next && (
              <Button
                variant="outline"
                className="h-auto min-w-0 flex-col items-end gap-0.5 py-2"
                render={<Link to={`/docs/${next.slug}`} />}
              >
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  Next <IconArrowRight className="size-3.5" />
                </span>
                <span className="truncate">{next.title}</span>
              </Button>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
